import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getTabOptions, NODE_TYPE } from './constants';
import { translate } from 'helpers/translateHelper';
import { GeneralButton } from 'sharedComponents/GeneralButton';
import { SearchBox } from 'sharedComponents/SearchBox';
import { IconSiteList } from 'svgIcons/MotionPortalIcons';
import IconLocation from 'svgIcons/MotionPortalIcons/IconLocation';
import HierarchyNode from 'sharedComponents/HierarchyView/Components/HierarchyNode';
import { getAssetIcon } from 'helpers/icons';
import { MAX_INPUT_CHAR_LENGTH } from 'helpers/constants';

const HierarchyExternalNodes = (props) => {

    const {
        nodeMap,
        selectedIds,
        powertrainNodes,
        activeId,
        overId,
        dropPosition,
        indent,
        renderNode,
        renderIcon,
        renderActions,
        dragEnabled,
        itemHeight,
        onContextMenu,
        selectedPowertrains,
        setSelectedPowertrains,
        searchboxFilter,
        setSearchboxFilter,
        assetNodes,
        selectedAssets,
        setSelectedAssets,
        assetListForSelectedNode,
        powertrainListForSelectedNode
    } = props;
    const tabOptions = getTabOptions();
    const [selectedTab, setSelectedTab] = useState(tabOptions[0]?.value);
    const [expandedIds, setExpandedIds] = useState([]);
    const selectedNodeData = nodeMap[selectedIds[0]];

    // Reset search and any selections when the active hierarchy node changes
    useEffect(() => {
        setSearchboxFilter('');
        setSelectedPowertrains([]);
        setSelectedAssets([]);
    }, [selectedIds[0]]);

    // Handle expand/collapse of external nodes
    const handleToggleExpand = useCallback((node) => {
        setExpandedIds(prev => {
            if (prev.includes(node.id)) {
                return prev.filter(id => id !== node.id);
            }
            return [...prev, node.id];
        });
    }, []);

    // Handle powertrain selection
    const handlePowertrainSelection = useCallback((powertrain) => {
        setSelectedPowertrains(prev => {
            if (prev.includes(powertrain)) {
                return prev.filter(id => id !== powertrain);
            }
            return [...prev, powertrain];
        });
    }, []);

    const handleAssetSelection = useCallback((asset) => {
        setSelectedAssets(prev => {
            if (prev.includes(asset)) {
                return prev.filter(id => id !== asset);
            }
            return [...prev, asset];
        });
    }, []);

    const renderSelectedTabContent = () => {
        switch (selectedTab) {
            case tabOptions[0]?.value: {
                return powertrainNodes?.map(externalNode => {
                    const nodeKey = externalNode?.id;
                    const isSelected = selectedPowertrains.includes(nodeKey);
                    const isDragging = activeId === nodeKey;
                    const isOver = overId === nodeKey;
                    const isExpanded = expandedIds.includes(nodeKey);
                    const children = externalNode?.data?.assets || [];

                    return (
                        <React.Fragment key={nodeKey}>
                            <HierarchyNode
                                node={externalNode}
                                level={0}
                                isSelected={isSelected}
                                isExpanded={isExpanded}
                                isDragging={isDragging}
                                isOver={isOver}
                                dropPosition={dropPosition}
                                indent={indent}
                                showExpandIcon={children?.length > 0}
                                showCheckbox={true}
                                onToggleExpand={handleToggleExpand}
                                onNodeClick={() => handlePowertrainSelection(nodeKey)}
                                onContextMenu={onContextMenu}
                                renderNode={renderNode}
                                renderIcon={(node) => {
                                    return renderIcon ? renderIcon(node) : null;
                                }}
                                renderActions={renderActions}
                                dragEnabled={dragEnabled}
                                getNodeKey={(node) => node.id}
                                isNodeSelectable={() => externalNode?.type === NODE_TYPE.POWERTRAIN}
                                style={{ height: itemHeight }}
                                editMode={false}
                                isEditing={false}
                                showDragHandle={dragEnabled}
                                childNodeCount={children?.length || 0}
                                showDragPreview={true}
                            />
                            <div className={`external-asset-nodes ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                {isExpanded && children.map(asset =>
                                    <div key={asset.id} className='external-asset-node'>
                                        <div className='asset-icon-container'>
                                            {getAssetIcon(asset.assetType, { width: '16px', height: '16px' })}
                                        </div>
                                        <div className='asset-info'>
                                            <div className='asset-name' title={asset.name}>
                                                {asset.name}
                                            </div>
                                            <div className='asset-id'>
                                                ({asset.id})
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </React.Fragment>
                    );
                });
            }
            case tabOptions[1]?.value: {
                return assetNodes?.map(externalNode => {
                    const nodeKey = externalNode?.id;
                    const isSelected = selectedAssets.includes(nodeKey);
                    const isDragging = activeId === nodeKey;
                    const isOver = overId === nodeKey;

                    return <HierarchyNode
                        key={nodeKey}
                        node={externalNode}
                        level={0}
                        isSelected={isSelected}
                        isExpanded={false}
                        isDragging={isDragging}
                        isOver={isOver}
                        dropPosition={dropPosition}
                        indent={indent}
                        showExpandIcon={false}
                        showCheckbox={true}
                        onToggleExpand={() => { }} // No expand/collapse for assets
                        onNodeClick={() => handleAssetSelection(nodeKey)}
                        onContextMenu={onContextMenu}
                        renderNode={renderNode}
                        renderIcon={(node) => {
                            return renderIcon ? renderIcon(node) : null;
                        }}
                        renderActions={renderActions}
                        dragEnabled={dragEnabled}
                        getNodeKey={(node) => node.id}
                        isNodeSelectable={() => externalNode?.type === NODE_TYPE.ASSET}
                        style={{ height: itemHeight }}
                        editMode={false}
                        isEditing={false}
                        showDragHandle={dragEnabled}
                        showDragPreview={true}
                    />;
                });
            }
            default:
                return null;
        }
    };

    const selectedNodeIcon = () => {
        if (selectedTab === tabOptions[0]?.value) {
            return <IconSiteList />;
        } else {
            return selectedNodeData?.type === NODE_TYPE.SITE ? <IconSiteList /> : <IconLocation />;
        }
    };

    const selectedNodeInfo = () => {
        if (selectedTab === tabOptions[0]?.value) {
            return nodeMap[selectedNodeData?.data?.siteId]?.name;
        } else {
            return selectedNodeData?.name;
        }
    };

    const getTabCount = (tabValue) => {
        if (tabValue === tabOptions[0]?.value) {
            return powertrainListForSelectedNode?.length || 0;
        } else {
            return assetListForSelectedNode?.length || 0;
        }
    };

    // True when every currently filtered item is selected (falls back to the full list when search is cleared)
    const areAllFilteredSelected = selectedTab === tabOptions[0]?.value
        ? powertrainNodes.length > 0 && powertrainNodes.every(node => selectedPowertrains.includes(node.id))
        : assetNodes?.length > 0 && assetNodes.every(node => selectedAssets.includes(node.id));

    const handleSelectAll = () => {
        if (selectedTab === tabOptions[0]?.value) {
            const filteredIds = powertrainNodes.map(node => node.id);
            if (areAllFilteredSelected) {
                // Unselect only the filtered powertrains, keeping other selections
                setSelectedPowertrains(selectedPowertrains.filter(id => !filteredIds.includes(id)));
            } else {
                // Select filtered powertrains while keeping existing selections
                const idsToAdd = filteredIds.filter(id => !selectedPowertrains.includes(id));
                setSelectedPowertrains([...selectedPowertrains, ...idsToAdd]);
            }
        } else if (selectedTab === tabOptions[1]?.value) {
            const filteredIds = assetNodes.map(node => node.id);
            if (areAllFilteredSelected) {
                // Unselect only the filtered assets, keeping other selections
                setSelectedAssets(selectedAssets.filter(id => !filteredIds.includes(id)));
            } else {
                // Select filtered assets while keeping existing selections
                const idsToAdd = filteredIds.filter(id => !selectedAssets.includes(id));
                setSelectedAssets([...selectedAssets, ...idsToAdd]);
            }
        }
    };

    const searchText = selectedTab === tabOptions[0]?.value
        ? translate('ABB.Powertrain.Frontend.searchPowertrainByName')
        : translate('ABB.Powertrain.Frontend.searchAssetByName');

    const renderNoData = () => {
        if (selectedTab === tabOptions[0]?.value && powertrainListForSelectedNode?.length === 0) {
            return <div className='no-data'>{translate('ABB.Powertrain.Frontend.noPowertrainsToShowLabel')}</div>;
        } else if (selectedTab === tabOptions[1]?.value && assetListForSelectedNode?.length === 0) {
            return <div className='no-data'>{translate('ABB.Powertrain.Frontend.noAssetsToShowLabel')} {selectedNodeData?.type}</div>;
        }
        return null;
    };

    const renderNoSearchResults = () => {
        if (selectedTab === tabOptions[0]?.value && powertrainNodes?.length === 0 ||
            selectedTab === tabOptions[1]?.value && assetNodes?.length === 0) {
            return <div className='no-data'>{translate('ABB.Powertrain.Frontend.noSearchResultsLabel')}</div>;
        }
        return null;
    };

    return <div className='external-nodes-container'>
        <div className='hierarchy-external-tabs'>
            {
                tabOptions.map((opt) => <button
                    key={opt.value}
                    className={`hierarchy-external-tab ${opt.value === selectedTab ? 'selected' : ''}`}
                    onClick={() => {
                        setSelectedTab(opt.value);
                        setSearchboxFilter('');
                        setSelectedPowertrains([]);
                        setSelectedAssets([]);
                    }}
                >
                    {opt.label} ({getTabCount(opt.value)})
                </button>)
            }
        </div>
        <div className='site-info'>
            {selectedNodeIcon()}
            <span>{selectedNodeInfo()}</span>
        </div>
        {renderNoData() ||
            <>
                <div className='search-powertrain'>
                    <SearchBox
                        onChange={setSearchboxFilter}
                        value={searchboxFilter}
                        placeholder={searchText}
                        maxLength={MAX_INPUT_CHAR_LENGTH}
                    />
                    {(selectedTab === tabOptions[0]?.value ? powertrainNodes?.length > 0 : assetNodes?.length > 0) && <GeneralButton
                        className='select-all-button'
                        type='discrete'
                        text={areAllFilteredSelected ? translate('ABB.Powertrain.Frontend.UnselectAllAssets') : translate('ABB.Powertrain.Frontend.SelectAllAssets')}
                        onClick={handleSelectAll}
                    />}
                </div>
                <div className='external-nodes-list'>
                    {renderNoSearchResults() || renderSelectedTabContent()}
                </div>
            </>
        }
    </div>;
};

HierarchyExternalNodes.propTypes = {
    nodeMap: PropTypes.object,
    selectedIds: PropTypes.array,
    powertrainNodes: PropTypes.array,
    activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    overId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dropPosition: PropTypes.string,
    indent: PropTypes.number,
    renderNode: PropTypes.func,
    renderIcon: PropTypes.func,
    renderActions: PropTypes.func,
    dragEnabled: PropTypes.bool,
    itemHeight: PropTypes.number,
    onContextMenu: PropTypes.func,
    selectedPowertrains: PropTypes.array,
    setSelectedPowertrains: PropTypes.func,
    searchboxFilter: PropTypes.string,
    setSearchboxFilter: PropTypes.func,
    assetNodes: PropTypes.array,
    selectedAssets: PropTypes.array,
    setSelectedAssets: PropTypes.func,
    assetListForSelectedNode: PropTypes.array,
    powertrainListForSelectedNode: PropTypes.array
};

export default HierarchyExternalNodes;
